import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError } from "../../lib/api";
import { authApi } from "../../lib/auth";
import { broadcastLogoutEvent } from "../../lib/authEvents";

type VerifyStatus = "verifying" | "success" | "error";

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const hasPostJobIntent = searchParams.get("intent") === "post-job";
  const loginPath = hasPostJobIntent ? "/login?redirect=%2Fdashboard%3Fintent%3Dpost-job" : "/login";
  const [status, setStatus] = useState<VerifyStatus>("verifying");
  const [message, setMessage] = useState("We are confirming your email now.");
  const attemptedQueryTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const queryToken = searchParams.get("token")?.trim() ?? "";

    if (!queryToken) {
      setStatus("error");
      setMessage("This verification link is missing a token. Return to login and request a new email if needed.");
      return;
    }

    if (attemptedQueryTokenRef.current === queryToken) {
      return;
    }

    attemptedQueryTokenRef.current = queryToken;

    const runVerification = async () => {
      setStatus("verifying");
      setMessage("We are confirming your email now.");

      try {
        await authApi.csrf();
        const result = await authApi.verifyEmail({ token: queryToken });
        broadcastLogoutEvent(result.mode === "EMAIL_CHANGE" ? "email_changed" : "email_verified");
        setStatus("success");
        setMessage(
          result.mode === "EMAIL_CHANGE"
            ? `Your email has been updated to ${result.email}. Please log in again with that address.`
            : "Your email has been verified. You can now log in to your account."
        );
      } catch (error) {
        setStatus("error");

        if (error instanceof ApiError && error.status === 410) {
          setMessage("This verification link has expired. Return to login and request a new verification email.");
          return;
        }

        if (error instanceof ApiError && error.status === 401) {
          setMessage("This verification link is invalid or has already been used.");
          return;
        }

        setMessage("We could not verify your email right now. Please try again later.");
      }
    };

    void runVerification();
  }, [searchParams]);

  const eyebrow =
    status === "success"
      ? "Email Verified"
      : status === "error"
        ? "Verification Problem"
        : "Verifying Email";

  const title =
    status === "success"
      ? "Verification complete"
      : status === "error"
        ? "We could not verify this email"
        : "Confirming your email";

  const panelClassName =
    status === "success"
      ? "border-emerald-200 bg-emerald-50/80"
      : status === "error"
        ? "border-red-200 bg-red-50/80"
        : "border-myteengig-border bg-myteengig-background/60";

  const accentClassName =
    status === "success"
      ? "text-emerald-700"
      : status === "error"
        ? "text-red-700"
        : "text-myteengig-primary";

  return (
    <main className="min-h-screen bg-myteengig-background px-6 py-16">
      <div className="mx-auto max-w-2xl overflow-hidden rounded-[32px] border border-myteengig-border bg-white shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
        <div className="bg-[linear-gradient(135deg,rgba(37,99,235,0.08),rgba(16,185,129,0.08))] px-8 py-10">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-myteengig-primary">{eyebrow}</p>
          <h1 className="mt-3 text-3xl font-bold text-myteengig-text-primary">{title}</h1>
          <p className="mt-4 max-w-xl text-base text-myteengig-text-muted">
            {status === "success"
              ? "Your verification link has been accepted and your account can now be accessed normally."
              : status === "error"
                ? "This page only works with a valid verification link from your email."
                : "Please wait while we validate the secure link from your email."}
          </p>
        </div>

        <div className="px-8 py-8">
          <div className={`rounded-2xl border p-5 ${panelClassName}`}>
            <p className={`text-sm font-semibold ${accentClassName}`}>
              {status === "verifying" ? "Verification in progress" : status === "success" ? "Verification complete" : "Action needed"}
            </p>
            <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">{message}</p>
          </div>

          <div className="mt-8">
            <Link
              to={loginPath}
              className="inline-flex items-center rounded-full bg-myteengig-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-myteengig-primary-hover"
            >
              {hasPostJobIntent ? "Continue to Post a Job" : "Go to login"}
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
};

export default VerifyEmailPage;
